<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { analysisTypesApi, analysisCategoriesApi, type AnalysisCategory, type AnalysisType } from '@/services/api/emsl';
import { useAppDialog } from '@/composables/useAppDialog';
import { useAppConfirm } from '@/composables/confirm';
import AnalysisTypeDialog from '@/components/dialogs/AnalysisTypeDialog.vue';

const items = ref<AnalysisType[]>([]);
const categories = ref<AnalysisCategory[]>([]);
const dialog = useAppDialog();
const confirm = useAppConfirm();

async function reload(): Promise<void> {
  [items.value, categories.value] = await Promise.all([
    analysisTypesApi.index(),
    analysisCategoriesApi.index(),
  ]);
}

async function openAdd(): Promise<void> {
  const { result } = dialog.open<Partial<AnalysisType>>(AnalysisTypeDialog, {
    header: 'Add Analysis Type',
    data: { categories: categories.value },
  });
  const data = await result;
  if (!data) return;
  const created = await analysisTypesApi.create(data);
  items.value = [...items.value, created];
}

async function openEdit(item: AnalysisType): Promise<void> {
  const { result } = dialog.open<Partial<AnalysisType>>(AnalysisTypeDialog, {
    header: 'Edit Analysis Type',
    data: { item, categories: categories.value },
  });
  const data = await result;
  if (!data) return;
  const updated = await analysisTypesApi.update({ ...data, id: item.id });
  items.value = items.value.map((i) => (i.id === updated.id ? { ...i, ...updated } : i));
}

async function remove(item: AnalysisType): Promise<void> {
  const ok = await confirm({ header: 'Remove Analysis Type', message: `Remove "${item.name}"?` });
  if (!ok) return;
  await analysisTypesApi.remove(item.id);
  items.value = items.value.filter((i) => i.id !== item.id);
}

onMounted(reload);
</script>

<template>
  <section class="resource">
    <header class="resource__header">
      <h1>Analysis Types</h1>
      <button type="button" class="resource__cta" @click="openAdd">+ Add Analysis Type</button>
    </header>
    <table class="resource__table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Category</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="t in items" :key="t.id">
          <td>{{ t.name }}</td>
          <td>{{ t.category }}</td>
          <td class="resource__actions">
            <button type="button" @click="openEdit(t)">Edit</button>
            <button type="button" @click="remove(t)">Remove</button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="items.length === 0">No analysis types.</p>
  </section>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.resource__cta {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font: inherit;
}
.resource__table {
  width: 100%;
  border-collapse: collapse;
}
.resource__table th,
.resource__table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}
.resource__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
</style>
